/**
 * Created by andreyna on 7/25/2016.
 */

/*
 * DateFormatter based on DateFormat of Steven Levithan <stevenlevithan.com>
 * Date Format 1.2.3
 * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
 * MIT license
 *
 * Accepts a date, a mask, or a date and a mask.
 * Returns a formatted version of the given date.
 * The date defaults to the current date/time.
 * The mask defaults to dateFormat.masks.default.
 *
 * Url: http://blog.stevenlevithan.com/archives/date-time-format
 */

class DateFormatter {

    private _token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;

    private _timezone = /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g;

    private _timezoneClip = /[^-+\dA-Z]/g;

    // Some common format strings
    private _masks = {
        "default": "ddd mmm dd yyyy HH:MM:ss",
        shortDate: "m/d/yy",
        mediumDate: "mmm d, yyyy",
        longDate: "mmmm d, yyyy",
        fullDate: "dddd, mmmm d, yyyy",
        shortTime: "h:MM TT",
        mediumTime: "h:MM:ss TT",
        longTime: "h:MM:ss TT Z",
        isoDate: "yyyy-mm-dd",
        isoTime: "HH:MM:ss",
        isoDateTime: "yyyy-mm-dd'T'HH:MM:ss",
        isoUtcDateTime: "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'",
        shortTime12: "h:MM TT",
        shortTime24: "HH:MM"
    }

    // Internationalization strings
    private _i18n = {
        dayNames: [
            "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat",
            "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
        ],
        monthNames: [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
            "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
        ]
    }


    /*
     * Samples:
     * format("longTime");
     * format("UTC:h:MM:ss TT Z");
     * format(now, "dddd, mmmm dS, yyyy, h:MM:ss TT")
     * format("Jun 9 2007", "fullDate");
     *
     * Default formats:
     * -------------------------------------------------------------------------
     * Name	Mask        Example
     * -------------------------------------------------------------------------
     * default      	ddd mmm dd yyyy HH:MM:ss	Sat Jun 09 2007 17:46:21
     * shortDate    	m/d/yy	6/9/07
     * mediumDate   	mmm d, yyyy	Jun 9, 2007
     * longDate     	mmmm d, yyyy	June 9, 2007
     * fullDate     	dddd, mmmm d, yyyy	Saturday, June 9, 2007
     * shortTime	    h:MM TT	5:46 PM
     * mediumTime	    h:MM:ss TT	5:46:21 PM
     * longTime	        h:MM:ss TT Z	5:46:21 PM EST
     * isoDate	        yyyy-mm-dd	2007-06-09
     * isoTime	        HH:MM:ss	17:46:21
     * isoDateTime	    yyyy-mm-dd'T'HH:MM:ss	2007-06-09T17:46:21
     * isoUtcDateTime	UTC:yyyy-mm-dd'T'HH:MM:ss'Z'	2007-06-09T22:46:21Z
     * -------------------------------------------------------------------------
     *
     * -------------------------------------------------------------------------
     * Masks:
     * -------------------------------------------------------------------------
     * Mask	    Description
     * -------------------------------------------------------------------------
     * d			Day of the month as digits; no leading zero for single-digit days.
     * dd			Day of the month as digits; leading zero for single-digit days.
     * ddd			Day of the week as a three-letter abbreviation.
     * dddd			Day of the week as its full name.
     * m			Month as digits; no leading zero for single-digit months.
     * mm			Month as digits; leading zero for single-digit months.
     * mmm			Month as a three-letter abbreviation.
     * mmmm			Month as its full name.
     * yy			Year as last two digits; leading zero for years less than 10.
     * yyyy			Year represented by four digits.
     * h			Hours; no leading zero for single-digit hours (12-hour clock).
     * hh			Hours; leading zero for single-digit hours (12-hour clock).
     * H			Hours; no leading zero for single-digit hours (24-hour clock).
     * HH			Hours; leading zero for single-digit hours (24-hour clock).
     * M			Minutes; no leading zero for single-digit minutes. Uppercase M unlike CF timeFormat's m to avoid conflict with months.
     * MM			Minutes; leading zero for single-digit minutes. Uppercase MM unlike CF timeFormat's mm to avoid conflict with months.
     * s			Seconds; no leading zero for single-digit seconds.
     * ss			Seconds; leading zero for single-digit seconds.
     * l or L		Milliseconds. l gives 3 digits. L gives 2 digits.
     * t			Lowercase, single-character time marker string: a or p.
     * tt			Lowercase, two-character time marker string: am or pm.
     * T			Uppercase, single-character time marker string: A or P. Uppercase T unlike CF's t to allow for user-specified casing.
     * TT			Uppercase, two-character time marker string: AM or PM. Uppercase TT unlike CF's tt to allow for user-specified casing.
     * Z			US timezone abbreviation, e.g. EST or MDT. With non-US timezones or in the Opera browser, the GMT/UTC offset is returned, e.g. GMT-0500.
     * o			GMT/UTC timezone offset, e.g. -0500 or +0230.
     * S			The date's ordinal suffix (st, nd, rd, or th). Works well with d.
     * '…' or "…"	Literal character sequence. Surrounding quotes are removed.
     * UTC			Must be the first four characters of the mask. Converts the date from local time to UTC/GMT/Zulu time before applying the mask. The "UTC:" prefix is removed.
     */
    format(date: any, mask?: string, utc?: boolean): string {
        // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
        if (arguments.length === 1 && Object.prototype.toString.call(date) === "[object String]" && !/\d/.test(date)) {
            mask = date;
            date = undefined;
        }

        // Passing date through Date applies Date.parse, if necessary
        date = date ? new Date(date) : new Date();
        if (isNaN(date)) {
            throw SyntaxError("invalid date");
        }

        mask = String(this._masks[mask] || mask || this._masks["default"]);

        // Allow setting the utc argument via the mask
        if (mask.slice(0, 4) === "UTC:") {
            mask = mask.slice(4);
            utc = true;
        }

        let _ = utc ? "getUTC" : "get",
            d = date[_ + "Date"](),
            D = date[_ + "Day"](),
            m = date[_ + "Month"](),
            y = date[_ + "FullYear"](),
            H = date[_ + "Hours"](),
            M = date[_ + "Minutes"](),
            s = date[_ + "Seconds"](),
            L = date[_ + "Milliseconds"](),
            o = utc ? 0 : date.getTimezoneOffset(),
            flags = {
                d: d,
                dd: this._pad(d),
                ddd: this._i18n.dayNames[D],
                dddd: this._i18n.dayNames[D + 7],
                m: m + 1,
                mm: this._pad(m + 1),
                mmm: this._i18n.monthNames[m],
                mmmm: this._i18n.monthNames[m + 12],
                yy: String(y).slice(2),
                yyyy: y,
                h: H % 12 || 12,
                hh: this._pad(H % 12 || 12),
                H: H,
                HH: this._pad(H),
                M: M,
                MM: this._pad(M),
                s: s,
                ss: this._pad(s),
                l: this._pad(L, 3),
                L: this._pad(L > 99 ? Math.round(L / 10) : L),
                t: H < 12 ? "a" : "p",
                tt: H < 12 ? "am" : "pm",
                T: H < 12 ? "A" : "P",
                TT: H < 12 ? "AM" : "PM",
                Z: utc ? "UTC" : (String(date).match(this._timezone) || [""]).pop().replace(this._timezoneClip, ""),
                o: (o > 0 ? "-" : "+") + this._pad(Math.floor(Math.abs(o) / 60) * 100 + Math.abs(o) % 60, 4),
                S: ["th", "st", "nd", "rd"][d % 10 > 3 ? 0 : ((d % 100 - d % 10) !== 10 ? 1 : 0) * d % 10]
            };

        return mask.replace(this._token, ($0) => {
            return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
        });
    }

    private _pad(val: number, len: number = 2): string {
        let valAsString = String(val);
        while (valAsString.length < len) {
            valAsString = "0" + valAsString;
        }
        return valAsString;
    }

    formatCurrentDateToUtc(): string {
        return this.format(new Date(), 'UTC:yyyy-mm-dd\'T\'HH:MM:ss\'Z\'');
    }

    formatUtc(date: Date): string {
        return this.format(date, 'UTC:yyyy-mm-dd\'T\'HH:MM:ss\'Z\'');
    }
}

export default new DateFormatter();